<div class="dashboard-container">
  <div class="sticky-header">
    <h1 class="dashboard-title">Pricing & Plans</h1>
    <app-date-range-selector
      [selectedPeriod]="selectedPeriod()"
      [startDate]="startDate()"
      [endDate]="endDate()"
      [includeWeek]="false"
      (periodChange)="onPeriodChange($event)"
      (dateRangeChange)="onDateRangeChange($event)"
    />
  </div>

  @if (pricingService.errorMessage(); as error) {
    <app-error-banner [message]="error" />
  }

  <div class="plans-grid">
    @for (plan of pricingService.planSummaries(); track plan.id) {
      <div class="plan-card-wrapper">
        <p-card styleClass="plan-card">
          <div class="plan-header">
            <h3>{{ plan.name }}</h3>
            <p-tag [value]="plan.totalOrgs + ' Orgs'" severity="info"></p-tag>
          </div>
          <div class="plan-price">
            {{ plan.price | currency: 'EUR' : 'symbol' : '1.0-0' }}
            <span>/ seat / mo</span>
          </div>
          <div class="plan-stats">
            <div class="stat-row">
              <span class="label">Total Seats Active</span>
              <span class="value">{{ plan.totalSeats }}</span>
            </div>
            <div class="stat-row">
              <span class="label">Est. Annual Revenue</span>
              <span class="value">{{
                plan.annualRevenue | currency: 'EUR' : 'symbol' : '1.0-0'
              }}</span>
            </div>
            <div class="stat-row">
              <span class="label">Limits (Docs/Email/Call/Live)</span>
              <span class="value text-xs"
                >{{ plan.limits.docs }} / {{ plan.limits.email }} /
                {{ plan.limits.calls | number }}s / {{ plan.limits.live | number }}s</span
              >
            </div>
          </div>
        </p-card>
      </div>
    }
  </div>

  <div class="table-wrapper">
    <p-card header="Plan Utilization & Cap Fulfillment">
      <div class="pricing-toolbar">
        <span class="p-input-icon-left">
          <i class="pi pi-search"></i>
          <input
            pInputText
            type="text"
            [ngModel]="search()"
            (ngModelChange)="search.set($event)"
            placeholder="Search organization or plan..."
            styleClass="pricing-search"
          />
        </span>
      </div>
      <p-table
        [value]="filteredUsage()"
        [paginator]="true"
        [rows]="10"
        styleClass="p-datatable-striped"
      >
        <ng-template pTemplate="header">
          <tr>
            <th pSortableColumn="orgName">Organisation <p-sortIcon field="orgName" /></th>
            <th pSortableColumn="planName">Plan <p-sortIcon field="planName" /></th>
            <th pSortableColumn="seats">Seats <p-sortIcon field="seats" /></th>
            <th>Users</th>
            <th>Documents</th>
            <th>Email practices</th>
            <th>Cold Calls practices</th>
            <th>Live Assistant sessions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
          <tr (click)="openDetail(item)" class="clickable-row">
            <td>
              <a
                class="link-cell"
                tabindex="0"
                role="link"
                (click)="openOrg(item.orgId); $event.stopPropagation()"
                (keydown.enter)="openOrg(item.orgId); $event.stopPropagation()"
              >
                {{ item.orgName }}
              </a>
            </td>
            <td><p-tag [value]="item.planName" severity="secondary"></p-tag></td>
            <td class="font-bold">{{ item.seats }}</td>

            <td>
              <div class="metric-cell">
                <div class="metric-text">
                  <span>{{ item.metrics.users.used }}</span>
                  <span class="text-500">/ {{ item.metrics.users.cap }}</span>
                </div>
                <div
                  class="utilization-bar"
                  [class]="getUtilizationClass(item.metrics.users.used, item.metrics.users.cap)"
                >
                  <div
                    class="bar-fill"
                    [style.width]="
                      getUtilizationPercent(item.metrics.users.used, item.metrics.users.cap) + '%'
                    "
                  ></div>
                </div>
              </div>
            </td>
            <td>
              <div class="metric-cell">
                <div class="metric-text">
                  <span>{{ item.metrics.documents.used }}</span>
                  <span class="text-500">/ {{ item.metrics.documents.cap }}</span>
                </div>
                <div
                  class="utilization-bar"
                  [class]="
                    getUtilizationClass(item.metrics.documents.used, item.metrics.documents.cap)
                  "
                >
                  <div
                    class="bar-fill"
                    [style.width]="
                      getUtilizationPercent(
                        item.metrics.documents.used,
                        item.metrics.documents.cap
                      ) + '%'
                    "
                  ></div>
                </div>
              </div>
            </td>
            <td>
              <div class="metric-cell">
                <div class="metric-text">
                  <span>{{ item.metrics.email.used }}</span>
                  <span class="text-500">/ {{ item.metrics.email.cap }}</span>
                </div>
                <div
                  class="utilization-bar"
                  [class]="getUtilizationClass(item.metrics.email.used, item.metrics.email.cap)"
                >
                  <div
                    class="bar-fill"
                    [style.width]="
                      getUtilizationPercent(item.metrics.email.used, item.metrics.email.cap) + '%'
                    "
                  ></div>
                </div>
              </div>
            </td>
            <td>
              <div class="metric-cell">
                <div class="metric-text">
                  <span>{{ item.metrics.calls.used / 60 | number: '1.0-0' }}m</span>
                  <span class="text-500"
                    >/ {{ item.metrics.calls.cap / 60 | number: '1.0-0' }}m</span
                  >
                </div>
                <div
                  class="utilization-bar"
                  [class]="getUtilizationClass(item.metrics.calls.used, item.metrics.calls.cap)"
                >
                  <div
                    class="bar-fill"
                    [style.width]="
                      getUtilizationPercent(item.metrics.calls.used, item.metrics.calls.cap) + '%'
                    "
                  ></div>
                </div>
              </div>
            </td>
            <td>
              <div class="metric-cell">
                <div class="metric-text">
                  <span>{{ item.metrics.live.used / 60 | number: '1.0-0' }}m</span>
                  <span class="text-500"
                    >/ {{ item.metrics.live.cap / 60 | number: '1.0-0' }}m</span
                  >
                </div>
                <div
                  class="utilization-bar"
                  [class]="getUtilizationClass(item.metrics.live.used, item.metrics.live.cap)"
                >
                  <div
                    class="bar-fill"
                    [style.width]="
                      getUtilizationPercent(item.metrics.live.used, item.metrics.live.cap) + '%'
                    "
                  ></div>
                </div>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </p-card>
  </div>
</div>

<p-dialog
  [(visible)]="showDetailSidebar"
  [modal]="true"
  [style]="{ width: '100%', height: '100%', maxHeight: '100%' }"
  [draggable]="false"
  [resizable]="false"
  header="Usage Breakdown"
  styleClass="p-dialog-maximized"
>
  @if (pricingService.selectedOrgDetail(); as detail) {
    <div class="flex flex-column gap-4">
      <div class="flex justify-content-between align-items-center">
        <div>
          <h2 class="m-0 mb-1 text-3xl font-bold text-900">{{ detail.orgName }}</h2>
          <p-tag [value]="detail.planName" severity="info" styleClass="text-lg"></p-tag>
        </div>
        <p-button
          label="Open organization"
          icon="pi pi-building"
          severity="secondary"
          [text]="true"
          (click)="openOrgForDetail()"
        ></p-button>
      </div>
      <p-card header="User Usage vs. Per-Seat Cap">
        <p-table [value]="detail.users" styleClass="p-datatable-sm" [rows]="10" [paginator]="true">
          <ng-template pTemplate="header">
            <tr>
              <th pSortableColumn="name">User <p-sortIcon field="name" /></th>
              <th>Documents</th>
              <th>Email practices</th>
              <th>Cold Call practices</th>
              <th>Live Assistant sessions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-user>
            <tr>
              <td>
                <a
                  class="link-cell"
                  tabindex="0"
                  role="link"
                  (click)="openUserSessions(user.id); $event.stopPropagation()"
                  (keydown.enter)="openUserSessions(user.id); $event.stopPropagation()"
                >
                  {{ user.name }}
                </a>
                <div class="text-xs text-500">{{ user.email }}</div>
              </td>
              <td>
                <div class="metric-cell">
                  <div class="metric-text">
                    <span>{{ user.metrics.documents.used }}</span>
                    <span class="text-500">/ {{ user.metrics.documents.cap }}</span>
                  </div>
                  <div
                    class="utilization-bar"
                    [class]="
                      getUtilizationClass(user.metrics.documents.used, user.metrics.documents.cap)
                    "
                  >
                    <div
                      class="bar-fill"
                      [style.width]="
                        getUtilizationPercent(
                          user.metrics.documents.used,
                          user.metrics.documents.cap
                        ) + '%'
                      "
                    ></div>
                  </div>
                </div>
              </td>
              <td>
                <div class="metric-cell">
                  <div class="metric-text">
                    <span>{{ user.metrics.email.used }}</span>
                    <span class="text-500">/ {{ user.metrics.email.cap }}</span>
                  </div>
                  <div
                    class="utilization-bar"
                    [class]="getUtilizationClass(user.metrics.email.used, user.metrics.email.cap)"
                  >
                    <div
                      class="bar-fill"
                      [style.width]="
                        getUtilizationPercent(user.metrics.email.used, user.metrics.email.cap) + '%'
                      "
                    ></div>
                  </div>
                </div>
              </td>
              <td>
                <div class="metric-cell">
                  <div class="metric-text">
                    <span>{{ user.metrics.calls.used / 60 | number: '1.0-0' }}m</span>
                    <span class="text-500"
                      >/ {{ user.metrics.calls.cap / 60 | number: '1.0-0' }}m</span
                    >
                  </div>
                  <div
                    class="utilization-bar"
                    [class]="getUtilizationClass(user.metrics.calls.used, user.metrics.calls.cap)"
                  >
                    <div
                      class="bar-fill"
                      [style.width]="
                        getUtilizationPercent(user.metrics.calls.used, user.metrics.calls.cap) + '%'
                      "
                    ></div>
                  </div>
                </div>
              </td>
              <td>
                <div class="metric-cell">
                  <div class="metric-text">
                    <span>{{ user.metrics.live.used / 60 | number: '1.0-0' }}m</span>
                    <span class="text-500"
                      >/ {{ user.metrics.live.cap / 60 | number: '1.0-0' }}m</span
                    >
                  </div>
                  <div
                    class="utilization-bar"
                    [class]="getUtilizationClass(user.metrics.live.used, user.metrics.live.cap)"
                  >
                    <div
                      class="bar-fill"
                      [style.width]="
                        getUtilizationPercent(user.metrics.live.used, user.metrics.live.cap) + '%'
                      "
                    ></div>
                  </div>
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    </div>
  } @else {
    <div class="flex align-items-center justify-content-center h-full">
      <i class="pi pi-spin pi-spinner text-3xl text-500"></i>
    </div>
  }
</p-dialog>
